import { Controller } from "@hotwired/stimulus"

declare global {
  interface Window {
    umami?: {
      track: (event: string, data?: Record<string, unknown>) => void;
    };
  }
}

// Lightbox dispatcher with three modes:
//   1. image — single output image (default)
//   2. video — single output video
//   3. upscale — original + 2 pixel-peek crops (template clone)
//   4. edit   — input(s) + output, side-by-side (template clone)
//
// Triggers carry data attributes that select the mode:
//   data-lightbox-src                   → image
//   data-lightbox-video-src             → video
//   data-lightbox-upscale-input + ...   → upscale (clones upscaleTemplate)
//   data-lightbox-edit-input + output   → edit    (clones editTemplate)
//
// upscale and edit modes clone a <template> into a host. Each open
// produces a fresh pixel-peek controller (where present) because
// Stimulus connects on DOM insertion — no manual reset needed.
export default class extends Controller {
  static targets = [
    "dialog", "image", "video", "videoWrapper", "loader", "prompt",
    "container", "meta",
    "upscaleTemplate", "upscaleHost",
    "editTemplate", "editHost",
    "sliderTemplate", "sliderHost"
  ]

  declare dialogTarget: HTMLDialogElement
  declare imageTarget: HTMLImageElement
  declare videoTarget: HTMLVideoElement
  declare videoWrapperTarget: HTMLElement
  declare loaderTarget: HTMLElement
  declare promptTarget: HTMLElement
  declare containerTarget: HTMLElement
  declare metaTarget: HTMLElement
  declare upscaleTemplateTarget: HTMLTemplateElement
  declare upscaleHostTarget: HTMLElement
  declare editTemplateTarget: HTMLTemplateElement
  declare editHostTarget: HTMLElement
  declare sliderTemplateTarget: HTMLTemplateElement
  declare sliderHostTarget: HTMLElement
  declare hasPromptTarget: boolean
  declare hasMetaTarget: boolean
  declare hasVideoWrapperTarget: boolean
  declare hasUpscaleTemplateTarget: boolean
  declare hasUpscaleHostTarget: boolean
  declare hasEditTemplateTarget: boolean
  declare hasEditHostTarget: boolean
  declare hasSliderTemplateTarget: boolean
  declare hasSliderHostTarget: boolean

  // Remembers the trigger that opened the current lightbox so the
  // keyboard nav handler can find its siblings within a
  // [data-lightbox-group] ancestor (e.g. a fanned skill stack).
  private currentTrigger: HTMLElement | null = null
  private boundKeyHandler = this.onKey.bind(this)
  // Tracks whether the document-level keydown listener is currently
  // attached so we only bind while the dialog is open and never
  // double-bind (idempotent add/remove).
  private keyListenerActive = false

  disconnect() {
    // Belt-and-braces: if the controller tears down mid-open, drop the
    // listener so it can't outlive the element.
    this.removeKeyListener()
  }

  open(event: Event) {
    event.preventDefault()
    const trigger = event.currentTarget as HTMLElement
    this.currentTrigger = trigger
    const d = trigger.dataset
    const type = d.lightboxUpscaleInput ? "upscale"
      : d.lightboxSliderInput ? "slider"
      : d.lightboxEditInput ? "edit"
      : d.lightboxVideoSrc ? "video"
      : "image"
    try { window.umami?.track("arena-lightbox-open", { type }) } catch {}
    this.dispatchOpen(trigger)
    // The dialog is shown synchronously by dispatchOpen's mode handlers;
    // bind keyboard nav now that it's open.
    this.addKeyListener()
  }

  // Attach the document keydown listener for arrow/escape nav. Guarded by
  // keyListenerActive so repeated opens (e.g. arrow-stepping) don't stack
  // duplicate listeners.
  private addKeyListener() {
    if (this.keyListenerActive) return
    document.addEventListener("keydown", this.boundKeyHandler)
    this.keyListenerActive = true
  }

  // Detach the keydown listener. Safe to call when already detached.
  private removeKeyListener() {
    if (!this.keyListenerActive) return
    document.removeEventListener("keydown", this.boundKeyHandler)
    this.keyListenerActive = false
  }

  private dispatchOpen(trigger: HTMLElement) {
    const src          = trigger.dataset.lightboxSrc
    const videoSrc     = trigger.dataset.lightboxVideoSrc
    const prompt       = trigger.dataset.lightboxPrompt
    const upscaleInput = trigger.dataset.lightboxUpscaleInput
    const sliderInput  = trigger.dataset.lightboxSliderInput
    const editInput    = trigger.dataset.lightboxEditInput

    if (upscaleInput && this.hasUpscaleTemplateTarget && this.hasUpscaleHostTarget) {
      this.openUpscale(trigger, prompt)
    } else if (sliderInput && this.hasSliderTemplateTarget && this.hasSliderHostTarget) {
      this.openSlider(trigger, prompt)
    } else if (editInput && this.hasEditTemplateTarget && this.hasEditHostTarget) {
      this.openEdit(trigger, prompt)
    } else if (videoSrc && this.hasVideoWrapperTarget) {
      this.openVideo(videoSrc, trigger, prompt)
    } else if (src) {
      this.openImage(src, trigger, prompt)
    }
  }

  // Keyboard navigation: ArrowLeft / ArrowRight cycle to the previous /
  // next trigger inside the same [data-lightbox-group] container, and
  // Escape closes. Only active when the dialog is open. If the current
  // trigger isn't inside a group, the arrows do nothing — keeps
  // single-tile pages predictable.
  private onKey(event: KeyboardEvent) {
    if (!this.dialogTarget.open) {
      // Stale listener; nothing is open. Clean up defensively.
      this.removeKeyListener()
      return
    }

    if (event.key === "Escape") {
      // The native <dialog> also closes on Escape; do it explicitly so
      // our close() bookkeeping (pause video, clear hosts, drop the
      // listener) always runs even if the host markup intercepts the
      // native cancel event.
      event.preventDefault()
      this.dialogTarget.close()
      return
    }

    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return
    const trigger = this.currentTrigger
    if (!trigger) return
    const group = trigger.closest("[data-lightbox-group]") as HTMLElement | null
    if (!group) return
    const peers = Array.from(
      group.querySelectorAll<HTMLElement>("[data-action~=\"click->lightbox#open\"]")
    )
    if (peers.length < 2) return
    const idx = peers.indexOf(trigger)
    if (idx < 0) return
    event.preventDefault()
    const nextIdx = event.key === "ArrowRight"
      ? (idx + 1) % peers.length
      : (idx - 1 + peers.length) % peers.length
    const next = peers[nextIdx]
    this.currentTrigger = next
    this.dispatchOpen(next)
  }

  // ── Upscale mode ────────────────────────────────────────────────────

  private openUpscale(trigger: HTMLElement, prompt: string | undefined) {
    const inputUrl  = trigger.dataset.lightboxUpscaleInput   || ""
    const outputUrl = trigger.dataset.lightboxUpscaleOutput  || ""
    const inputW    = trigger.dataset.lightboxUpscaleInputW  || ""
    const inputH    = trigger.dataset.lightboxUpscaleInputH  || ""
    const outputW   = trigger.dataset.lightboxUpscaleOutputW || ""
    const outputH   = trigger.dataset.lightboxUpscaleOutputH || ""
    const scale     = trigger.dataset.lightboxUpscaleScale   || ""

    this.resetSurfaces()
    this.containerTarget.classList.add("is-upscale")

    const fragment = this.upscaleTemplateTarget.content.cloneNode(true) as DocumentFragment
    const refImg    = fragment.querySelector<HTMLImageElement>('[data-upscale-role="refImg"]')
    const lensOrig  = fragment.querySelector<HTMLElement>('[data-upscale-role="lensOrig"]')
    const lensUp    = fragment.querySelector<HTMLElement>('[data-upscale-role="lensUp"]')
    const ratioWrap = fragment.querySelector<HTMLElement>('[data-upscale-role="ratio"]')

    if (refImg) refImg.src = inputUrl
    if (lensOrig) {
      lensOrig.dataset.pixelPeekSrc           = inputUrl
      lensOrig.dataset.pixelPeekNaturalWidth  = inputW
      lensOrig.dataset.pixelPeekNaturalHeight = inputH
    }
    if (lensUp) {
      lensUp.dataset.pixelPeekSrc           = outputUrl
      lensUp.dataset.pixelPeekNaturalWidth  = outputW
      lensUp.dataset.pixelPeekNaturalHeight = outputH
    }
    if (ratioWrap && scale) {
      ratioWrap.removeAttribute("hidden")
      const num = ratioWrap.querySelector<HTMLElement>("[data-ratio-num]")
      if (num) num.textContent = scale
    }

    this.setupOverlays(trigger, prompt)
    this.loaderTarget.classList.add("hidden")
    this.upscaleHostTarget.appendChild(fragment)
    this.dialogTarget.showModal()
  }

  // ── Slider mode (image-comparison drag-to-reveal) ───────────────────
  //
  // For image_editing competitions where use_comparison_slider is on.
  // Cloning the template inserts a fresh image-comparison controller
  // — Stimulus mounts on insertion, no manual setup.
  private openSlider(trigger: HTMLElement, prompt: string | undefined) {
    const inputUrl  = trigger.dataset.lightboxSliderInput  || ""
    const outputUrl = trigger.dataset.lightboxSliderOutput || ""

    this.resetSurfaces()
    this.containerTarget.classList.add("is-upscale") // reuse the layout neutraliser

    const fragment = this.sliderTemplateTarget.content.cloneNode(true) as DocumentFragment
    const before = fragment.querySelector<HTMLImageElement>('[data-slider-role="beforeImg"]')
    const after  = fragment.querySelector<HTMLImageElement>('[data-slider-role="afterImg"]')
    if (before) before.src = inputUrl
    if (after)  after.src  = outputUrl

    this.setupOverlays(trigger, prompt)
    this.loaderTarget.classList.add("hidden")
    this.sliderHostTarget.appendChild(fragment)
    this.dialogTarget.showModal()
  }

  // ── Edit mode (input + output, side-by-side) ────────────────────────
  //
  // For image_editing rows, show the input(s) on one side and the output
  // on the other. Multiple inputs come in via comma-separated URLs in
  // data-lightbox-edit-input — they stack vertically inside the input
  // panel.
  private openEdit(trigger: HTMLElement, prompt: string | undefined) {
    const inputCsv  = trigger.dataset.lightboxEditInput  || ""
    const outputUrl = trigger.dataset.lightboxEditOutput || ""
    const inputUrls = inputCsv.split(",").map(s => s.trim()).filter(Boolean)

    this.resetSurfaces()
    this.containerTarget.classList.add("is-upscale") // same neutralisation as upscale (no portrait/landscape sizing)

    const fragment = this.editTemplateTarget.content.cloneNode(true) as DocumentFragment
    const inputCol = fragment.querySelector<HTMLElement>('[data-edit-role="inputCol"]')
    const outImg   = fragment.querySelector<HTMLImageElement>('[data-edit-role="outputImg"]')

    if (inputCol) {
      inputCol.replaceChildren()
      inputUrls.forEach((url, i) => {
        const wrap = document.createElement("div")
        wrap.className = "lightbox-edit__panel"
        const img = document.createElement("img")
        img.src = url
        img.alt = inputUrls.length > 1 ? `Input image ${i + 1}` : "Input image"
        img.decoding = "async"
        wrap.appendChild(img)
        const lbl = document.createElement("span")
        lbl.className = "lightbox-edit__label"
        lbl.textContent = inputUrls.length > 1 ? `INPUT ${i + 1}` : "INPUT"
        wrap.appendChild(lbl)
        inputCol.appendChild(wrap)
      })
    }
    if (outImg) outImg.src = outputUrl

    this.setupOverlays(trigger, prompt)
    this.loaderTarget.classList.add("hidden")
    this.editHostTarget.appendChild(fragment)
    this.dialogTarget.showModal()
  }

  // ── Single image / video ────────────────────────────────────────────

  private openImage(src: string, trigger: HTMLElement, prompt: string | undefined) {
    this.resetSurfaces()
    this.imageTarget.style.display = ""
    this.imageTarget.classList.add("opacity-0")
    this.loaderTarget.classList.remove("hidden")

    const alt = trigger.dataset.lightboxAlt || trigger.querySelector("img")?.alt || ""
    this.imageTarget.alt = alt

    // Size the frame from the known intrinsic dimensions BEFORE showing the
    // dialog, so the box opens at its final size with the spinner centred in
    // it and the image fades in without the collapse→expand jump. Falls back
    // to post-load measurement when dimensions are unknown.
    const presized = this.presizeFromDimensions(
      parseInt(trigger.dataset.lightboxW || "", 10),
      parseInt(trigger.dataset.lightboxH || "", 10)
    )

    this.setupOverlays(trigger, prompt)
    this.dialogTarget.showModal()

    this.imageTarget.onload = () => {
      this.loaderTarget.classList.add("hidden")
      this.imageTarget.classList.remove("opacity-0")
      // When we couldn't pre-size, fall back to measuring the loaded image.
      if (!presized) this.adjustForAspectRatio()
    }
    this.imageTarget.src = src
  }

  // Reserve the exact final image box from intrinsic width/height, reproducing
  // the CSS constraints (.lightbox-image max-height 66vh; container max-width
  // min(90vw, 1200px)). aspect-ratio holds the image's height before its src
  // loads, and the container width is pinned now so the dialog opens at its
  // settled size. Returns false (→ post-load measurement) when dims are bad.
  private presizeFromDimensions(w: number, h: number): boolean {
    if (!(w > 0 && h > 0)) return false
    const aspect = w / h
    const maxW = Math.min(window.innerWidth * 0.9, 1200)
    const maxH = window.innerHeight * 0.66 // matches .lightbox-image max-height: calc(90vh - 24vh)
    let dispW = maxW
    let dispH = dispW / aspect
    if (dispH > maxH) { dispH = maxH; dispW = dispH * aspect }

    if (aspect < 1) {
      this.containerTarget.classList.add("lightbox-portrait")
      this.containerTarget.classList.remove("lightbox-landscape")
    } else {
      this.containerTarget.classList.add("lightbox-landscape")
      this.containerTarget.classList.remove("lightbox-portrait")
    }
    this.imageTarget.style.aspectRatio = `${w} / ${h}`
    this.imageTarget.style.width = `${dispW}px`
    // !important to beat .lightbox-container's `width: fit-content !important`.
    this.containerTarget.style.setProperty("width", `${dispW}px`, "important")
    return true
  }

  private openVideo(src: string, trigger: HTMLElement, prompt: string | undefined) {
    this.resetSurfaces()
    this.imageTarget.style.display = "none"
    this.imageTarget.src = ""
    this.videoWrapperTarget.style.display = ""

    this.loaderTarget.classList.remove("hidden")
    this.setupOverlays(trigger, prompt)

    this.containerTarget.style.maxWidth = ""
    this.containerTarget.style.maxHeight = ""
    this.containerTarget.classList.add("lightbox-landscape", "lightbox-video")
    this.containerTarget.classList.remove("lightbox-portrait")

    this.dialogTarget.showModal()

    // The <video> lives inside @videojs/html's <video-player><video-skin>
    // web components, which proxy the media element. Relying solely on a
    // single `onloadeddata` handler left the spinner stuck forever when
    // that event didn't surface on the bare element. Hide the loader on
    // any of the events that signal the video is ready to show a frame,
    // and once playback actually starts.
    const video = this.videoTarget
    const hideLoader = () => this.loaderTarget.classList.add("hidden")
    const events = ["loadeddata", "loadedmetadata", "canplay", "playing"]
    const onReady = () => {
      hideLoader()
      events.forEach((e) => video.removeEventListener(e, onReady))
    }
    events.forEach((e) => video.addEventListener(e, onReady))

    video.src = src
    video.load()
    video.play().then(hideLoader).catch(() => {
      // Autoplay can be blocked or interrupted; the video is still loaded
      // and visible, so don't leave the spinner up.
      hideLoader()
    })
  }

  // Bring the dialog back to a known state before opening in any mode.
  // Empties the template hosts so cloned controllers (pixel-peek) get
  // disconnected, hides the single-image and video surfaces, drops mode
  // classes off the container.
  private resetSurfaces() {
    this.imageTarget.style.display = "none"
    this.imageTarget.removeAttribute("src")
    // Drop any pre-sized frame so the next open recomputes (portrait↔landscape).
    this.imageTarget.style.removeProperty("aspect-ratio")
    this.imageTarget.style.removeProperty("width")
    this.imageTarget.style.removeProperty("height")
    if (this.hasVideoWrapperTarget) {
      this.videoWrapperTarget.style.display = "none"
      this.videoTarget.pause()
      this.videoTarget.removeAttribute("src")
    }
    if (this.hasUpscaleHostTarget) this.upscaleHostTarget.replaceChildren()
    if (this.hasEditHostTarget)    this.editHostTarget.replaceChildren()
    if (this.hasSliderHostTarget)  this.sliderHostTarget.replaceChildren()
    this.containerTarget.classList.remove("is-upscale", "lightbox-portrait", "lightbox-landscape", "lightbox-video")
    // Drop the per-image width pin so the next open computes fresh.
    this.containerTarget.style.removeProperty("width")
  }

  private setupOverlays(trigger: HTMLElement, prompt: string | undefined) {
    if (this.hasPromptTarget) {
      if (prompt) {
        this.promptTarget.textContent = prompt
        this.promptTarget.classList.remove("hidden")
      } else {
        this.promptTarget.classList.add("hidden")
      }
    }
    if (this.hasMetaTarget) {
      const meta = trigger.dataset.lightboxMeta
      if (meta) {
        this.metaTarget.textContent = meta
        this.metaTarget.classList.remove("hidden")
      } else {
        this.metaTarget.classList.add("hidden")
      }
    }
  }

  // Fires on the dialog's native `close` event (Escape, backdrop click,
  // explicit close button), so this is the single reliable place to drop
  // the keyboard listener bound while open.
  close() {
    this.removeKeyListener()
    this.currentTrigger = null
    if (this.hasVideoWrapperTarget) {
      this.videoTarget.pause()
      this.videoTarget.removeAttribute("src")
    }
    if (this.hasUpscaleHostTarget) this.upscaleHostTarget.replaceChildren()
    if (this.hasEditHostTarget)    this.editHostTarget.replaceChildren()
    if (this.hasSliderHostTarget)  this.sliderHostTarget.replaceChildren()
    this.containerTarget.classList.remove("is-upscale")
  }

  private adjustForAspectRatio() {
    const img = this.imageTarget
    if (!img.naturalWidth || !img.naturalHeight) {
      // SVG (or not-yet-measured): let CSS size it; skip portrait/landscape pinning.
      this.containerTarget.classList.remove("lightbox-portrait", "lightbox-landscape")
      return
    }
    const aspectRatio = img.naturalWidth / img.naturalHeight
    this.containerTarget.style.maxWidth = ""
    this.containerTarget.style.maxHeight = ""
    if (aspectRatio < 1) {
      this.containerTarget.classList.add("lightbox-portrait")
      this.containerTarget.classList.remove("lightbox-landscape")
    } else {
      this.containerTarget.classList.add("lightbox-landscape")
      this.containerTarget.classList.remove("lightbox-portrait")
    }
    // Pin the container width to the image's rendered width so the
    // prompt band beneath sits flush with the image edges (rather than
    // stretching out to the modal-box max-width). setProperty with the
    // !important flag is required to beat the !important on the CSS
    // fallback rule that defeats DaisyUI's .modal-box width.
    requestAnimationFrame(() => {
      const renderedWidth = img.getBoundingClientRect().width
      // Inline SVGs can report natural dimensions while collapsing to zero
      // inside the fit-content modal. Give them a useful viewport-sized width.
      const fallbackWidth = Math.min(window.innerWidth * 0.9, window.innerHeight * 0.66 * aspectRatio)
      const w = renderedWidth > 0 ? renderedWidth : fallbackWidth
      if (w > 0) this.containerTarget.style.setProperty("width", `${w}px`, "important")
    })
  }
}
